import { useCallback, useEffect, useRef } from 'react';

/**
 * FIXME: This hook has accumulated complexity and could benefit from refactoring
 *
 * Current responsibilities (too many for one hook):
 * 1. Long-press detection (timer-based)
 * 2. Click detection (when not a long press)
 * 3. Movement cancellation (finger moved too far)
 * 4. Touch vs mouse event differentiation
 * 5. Synthetic click prevention (with interactive element exceptions)
 * 6. Cleanup on unmount
 *
 * Signs of accidental complexity:
 * - The `'touches' in event` type-narrowing pattern appears multiple times
 * - Hook checks `target.closest()` for buttons/inputs — leaky abstraction
 * - The hook "knows" about ARIA roles, which couples it to DOM structure
 *
 * Alternative approaches to consider:
 * - Option A: Separate concerns — split into pure gesture detection, let consumers
 *   handle click prevention at their level
 * - Option B: CSS `touch-action: manipulation` — disables double-tap zoom and
 *   some synthetic events, potentially avoiding JS complexity
 * - Option C: Pointer Events API — `onPointerDown`/`onPointerUp` unifies touch
 *   and mouse, potentially simplifying the event type branching
 */

export interface LongPressOptions {
  onLongPress: (event: TouchEvent | MouseEvent) => void;
  onClick?: (event: TouchEvent | MouseEvent) => void;
  threshold?: number;
  onStart?: () => void;
  onCancel?: () => void;
}

export interface LongPressHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchEnd: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onMouseDown?: (event: React.MouseEvent) => void;
  onMouseUp?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
}

/**
 * Hook to detect long-press gestures on touch devices
 *
 * @param options - Configuration options for long-press behavior
 * @returns Event handlers to attach to the target element
 *
 * @example
 * ```tsx
 * const longPressHandlers = useLongPress({
 *   onLongPress: () => console.log('Long pressed!'),
 *   onClick: () => console.log('Clicked!'),
 *   threshold: 500,
 * });
 *
 * return <div {...longPressHandlers}>Press me</div>;
 * ```
 */
export function useLongPress(
  options: LongPressOptions,
  deps: React.DependencyList = [],
): LongPressHandlers {
  const { onLongPress, onClick, threshold = 500, onStart, onCancel } = options;

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const eventRef = useRef<TouchEvent | MouseEvent | null>(null);
  const isLongPressRef = useRef(false);
  const wasCancelledRef = useRef(false);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);

  const start = useCallback(
    (event: TouchEvent | MouseEvent) => {
      // Store the starting position
      if ('touches' in event) {
        startPositionRef.current = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
      } else {
        startPositionRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
      }

      eventRef.current = event;
      isLongPressRef.current = false;
      wasCancelledRef.current = false;

      onStart?.();

      timerRef.current = setTimeout(() => {
        isLongPressRef.current = true;
        onLongPress(event);
      }, threshold);
    },
    [onLongPress, threshold, onStart, ...deps],
  );

  const cancel = useCallback(
    (wasMoved = false) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (wasMoved) {
        wasCancelledRef.current = true;
        onCancel?.();
      } else if (isLongPressRef.current) {
        onCancel?.();
      }

      startPositionRef.current = null;
      eventRef.current = null;
      isLongPressRef.current = false;
    },
    [onCancel, ...deps],
  );

  const end = useCallback(
    (event: TouchEvent | MouseEvent) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // A completed long press must not also fire the synthetic click that
      // follows touchend — otherwise opening the menu on a link navigates away
      // underneath it.
      if (isLongPressRef.current && 'touches' in event && event.cancelable) {
        event.preventDefault();
      }

      // Only trigger onClick if:
      // 1. It was NOT a long press
      // 2. It was NOT cancelled due to movement
      // 3. onClick handler exists
      if (!isLongPressRef.current && !wasCancelledRef.current && onClick) {
        // Prevent synthetic mouse events (click) from firing after touch events
        // This prevents double-firing on mobile devices.
        // HOWEVER: Don't preventDefault if the touch target was an interactive element
        // (button, input, etc.) - those need their synthetic click to function properly.
        if ('touches' in event) {
          const target = event.target as HTMLElement;
          const isInteractiveElement = target.closest(
            'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="menuitem"]',
          );
          if (!isInteractiveElement) {
            event.preventDefault();
          }
        }
        onClick(event);
      }

      startPositionRef.current = null;
      eventRef.current = null;
      isLongPressRef.current = false;
      wasCancelledRef.current = false;
    },
    [onClick, ...deps],
  );

  const move = useCallback(
    (event: TouchEvent) => {
      // Cancel long press if the finger moves too far
      if (startPositionRef.current && timerRef.current) {
        const currentX = event.touches[0].clientX;
        const currentY = event.touches[0].clientY;
        const deltaX = Math.abs(currentX - startPositionRef.current.x);
        const deltaY = Math.abs(currentY - startPositionRef.current.y);

        // Cancel if moved more than 10px in any direction
        if (deltaX > 10 || deltaY > 10) {
          cancel(true);
        }
      }
    },
    [cancel, ...deps],
  );

  // Cleanup timer on unmount to prevent stale handlers from firing
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    onTouchStart: useCallback(
      (e: React.TouchEvent) => start(e.nativeEvent),
      [start],
    ),
    onTouchEnd: useCallback((e: React.TouchEvent) => end(e.nativeEvent), [end]),
    onTouchMove: useCallback(
      (e: React.TouchEvent) => move(e.nativeEvent),
      [move],
    ),
  };
}
